import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FormData } from '../types';

interface TextEditorProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}

const TextEditor: React.FC<TextEditorProps> = ({ formData, setFormData }) => {
  const [charCount, setCharCount] = useState(0);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ left: 0, top: 0 });
  const [toolbarBelow, setToolbarBelow] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    link: false
  });
  
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const isUpdatingFromInput = useRef(false);

  // Utility function to save and restore cursor position
  const saveCursorPosition = useCallback(() => {
    if (!editorRef.current) return null;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const caretOffset = preCaretRange.toString().length;
    
    return caretOffset;
  }, []);

  const restoreCursorPosition = useCallback((caretOffset: number) => {
    if (!editorRef.current) return;
    
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let currentOffset = 0;
    let node;
    
    while ((node = walker.nextNode()) !== null) {
      const nodeLength = node.textContent?.length || 0;
      if (currentOffset + nodeLength >= caretOffset) {
        const range = document.createRange();
        const selection = window.getSelection();
        const targetOffset = caretOffset - currentOffset;
        
        range.setStart(node, Math.min(targetOffset, nodeLength));
        range.collapse(true);
        
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        break;
      }
      currentOffset += nodeLength;
    }
  }, []);

  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      isUpdatingFromInput.current = true;
      const htmlContent = editorRef.current.innerHTML;
      const textContent = editorRef.current.textContent || editorRef.current.innerText || '';
      
      setCharCount(textContent.length);
      
      // Enforce character limit
      if (textContent.length > 300) {
        const trimmedContent = textContent.substring(0, 300);
        editorRef.current.textContent = trimmedContent;
        
        // Move cursor to end
        const range = document.createRange();
        const selection = window.getSelection();
        if (selection) {
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        isUpdatingFromInput.current = false;
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        adContent: htmlContent,
        adTextContent: textContent
      }));
      
      // Reset the flag after a short delay to allow the useEffect to run
      setTimeout(() => {
        isUpdatingFromInput.current = false;
      }, 0);
    }
  }, [setFormData]);

  const updateFormatButtonStates = useCallback(() => {
    if (!editorRef.current || document.activeElement !== editorRef.current) return;
    
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      link: document.queryCommandState('createLink')
    });
  }, []);

  const showFormattingToolbar = useCallback((selection: Selection) => {
    if (!editorRef.current || !toolbarRef.current) return;
    
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const editorRect = editorRef.current.getBoundingClientRect();
    
    // Position toolbar above the selection
    const toolbarHeight = 40;
    const toolbarWidth = 120;
    
    // Calculate position relative to the editor container
    const centerX = rect.left - editorRect.left + (rect.width / 2) - (toolbarWidth / 2);
    const topY = rect.top - editorRect.top - toolbarHeight - 10;
    const bottomY = rect.bottom - editorRect.top + 10;
    
    // Ensure toolbar stays within horizontal bounds
    const maxLeft = editorRef.current.offsetWidth - toolbarWidth;
    const adjustedLeft = Math.max(0, Math.min(centerX, maxLeft));
    
    // Choose whether to show above or below based on available space
    let adjustedTop;
    let showBelow = false;
    
    if (topY < 10) {
      // Not enough space above, show below
      adjustedTop = bottomY;
      showBelow = true;
    } else {
      // Enough space above, show above
      adjustedTop = topY;
      showBelow = false;
    }
    
    // Ensure toolbar doesn't go below editor bounds
    if (showBelow && adjustedTop + toolbarHeight > editorRef.current.offsetHeight - 10) {
      adjustedTop = editorRef.current.offsetHeight - toolbarHeight - 10;
    }
    
    setToolbarPosition({ left: adjustedLeft, top: adjustedTop });
    setToolbarBelow(showBelow);
    setToolbarVisible(true);
    
    updateFormatButtonStates();
  }, [updateFormatButtonStates]);

  const hideFormattingToolbar = useCallback(() => {
    setToolbarVisible(false);
  }, []);

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    
    if (selectedText && selectedText.length > 0 && editorRef.current?.contains(selection?.anchorNode || null)) {
      showFormattingToolbar(selection!);
    } else {
      hideFormattingToolbar();
    }
  }, [showFormattingToolbar, hideFormattingToolbar]);

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    
    if (!selectedText || selectedText.length === 0 || !editorRef.current?.contains(selection?.anchorNode || null)) {
      hideFormattingToolbar();
    }
    
    updateFormatButtonStates();
  }, [hideFormattingToolbar, updateFormatButtonStates]);

  const applyFormat = useCallback((format: string) => {
    if (!editorRef.current) return;
    
    editorRef.current.focus();
    
    switch (format) {
      case 'bold':
        document.execCommand('bold', false, '');
        break;
      case 'italic':
        document.execCommand('italic', false, '');
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          document.execCommand('createLink', false, url);
        }
        break;
    }
    
    // Update preview and character count
    handleContentChange();
    updateFormatButtonStates();
  }, [handleContentChange, updateFormatButtonStates]);

  const getCharCountClass = () => {
    if (charCount > 270) return 'char-counter error';
    if (charCount > 240) return 'char-counter warning';
    return 'char-counter';
  };

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch(e.key) {
        case 'b':
          e.preventDefault();
          applyFormat('bold');
          break;
        case 'i':
          e.preventDefault();
          applyFormat('italic');
          break;
        case 'k':
          e.preventDefault();
          applyFormat('link');
          break;
      }
    }
  }, [applyFormat]);

  // Handle paste to clean up content
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    document.execCommand('insertText', false, text);
  }, []);

  useEffect(() => {
    // Load saved content only when not updating from user input
    if (formData.adContent && editorRef.current && !isUpdatingFromInput.current) {
      const currentContent = editorRef.current.innerHTML;
      // Only update innerHTML if the content is different and we're not in the middle of user input
      if (currentContent !== formData.adContent) {
        // Save cursor position before updating content
        const cursorPosition = saveCursorPosition();
        
        editorRef.current.innerHTML = formData.adContent;
        setCharCount(formData.adTextContent.length);
        
        // Restore cursor position after updating content
        if (cursorPosition !== null) {
          setTimeout(() => restoreCursorPosition(cursorPosition), 0);
        }
      }
    }
  }, [formData.adContent, formData.adTextContent.length, saveCursorPosition, restoreCursorPosition]);

  useEffect(() => {
    // Add global event listeners
    document.addEventListener('selectionchange', handleSelectionChange);
    
    // Click outside handler
    const handleClickOutside = (e: MouseEvent) => {
      if (!editorRef.current?.contains(e.target as Node) && 
          !toolbarRef.current?.contains(e.target as Node)) {
        hideFormattingToolbar();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleSelectionChange, hideFormattingToolbar]);

  return (
    <div className="text-editor-container">
      <div
        ref={editorRef}
        className="text-editor"
        contentEditable
        data-placeholder="✍️ Write your ad — tell people why they should click! (Max 300 chars)"
        onInput={handleContentChange}
        onBlur={handleContentChange}
        onMouseUp={handleTextSelection}
        onKeyUp={handleTextSelection}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        style={{ 
          minHeight: '120px',
          outline: 'none'
        }}
      />
      
      {/* Formatting Toolbar */}
      <div 
        ref={toolbarRef}
        className={`formatting-toolbar ${toolbarVisible ? 'visible' : ''} ${toolbarBelow ? 'below' : ''}`}
        style={{
          left: `${toolbarPosition.left}px`,
          top: `${toolbarPosition.top}px`
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <button
          className={`format-btn ${activeFormats.bold ? 'active' : ''}`}
          onClick={() => applyFormat('bold')}
          data-format="bold"
        >
          <strong>B</strong>
        </button>
        <button
          className={`format-btn ${activeFormats.italic ? 'active' : ''}`}
          onClick={() => applyFormat('italic')}
          data-format="italic"
        >
          <em>I</em>
        </button>
        <button
          className={`format-btn ${activeFormats.link ? 'active' : ''}`}
          onClick={() => applyFormat('link')}
          data-format="link"
        >
          🔗
        </button>
      </div>
      
      <div className={getCharCountClass()}>
        {charCount}/300
      </div>
      <div className="example-text">
        e.g., "🔥 New <strong>AI photo app</strong>! Make your selfies go viral 📸"
      </div>
    </div>
  );
};

export default TextEditor; 